import { AiVariableOption } from "./aiConfig.types";
import { findAiVariableOption, resolveAiVariableToken } from "./aiConfig.utils";

const TOKEN_REGEX = /\{\{\s*([^}]+?)\s*\}\}/g;

const escapeHtml = (value: string) =>
	value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");

const decodeHtml = (value: string) => {
	const textArea = document.createElement("textarea");

	textArea.innerHTML = value;

	return textArea.value;
};

const buildFallbackVariable = (token: string): AiVariableOption => ({
	key: token.replace(/^\{\{|\}\}$/g, ""),
	token,
	label: token.replace(/^\{\{|\}\}$/g, ""),
	group: "Custom",
});

export const buildAiVariableChipHtml = (option: AiVariableOption) =>
	`<span class="mceNonEditable ai-variable-chip" data-ai-variable="${escapeHtml(
		option.token
	)}" contenteditable="false">${escapeHtml(option.label)}</span>`;

export const promptToEditorHtml = (prompt: string) => {
	const escapedPrompt = escapeHtml(prompt);

	return escapedPrompt
		.replace(TOKEN_REGEX, (_match, rawKey: string) => {
			const token = resolveAiVariableToken(rawKey);
			const option = findAiVariableOption(token) ?? buildFallbackVariable(token);

			return buildAiVariableChipHtml(option);
		})
		.replace(/\n/g, "<br />");
};

export const editorHtmlToPromptText = (html: string) => {
	const withTokens = html
		.replace(
			/<span[^>]*data-ai-variable="([^"]+)"[^>]*>[\s\S]*?<\/span>/gi,
			(_match, token: string) => decodeHtml(token)
		)
		.replace(/<br\s*\/?>/gi, "\n")
		.replace(/<\/(p|div|li|h1|h2|h3)>/gi, "\n")
		.replace(/<(p|div|h1|h2|h3)[^>]*>/gi, "")
		.replace(/<li[^>]*>/gi, "- ")
		.replace(/&nbsp;/gi, " ")
		.replace(/<[^>]+>/g, "");

	return decodeHtml(withTokens)
		.replace(/\n{3,}/g, "\n\n")
		.trim();
};

export const filterAiVariableOptions = (options: AiVariableOption[], pattern: string) => {
	const normalizedPattern = pattern.trim().toLowerCase();

	if (!normalizedPattern) {
		return options;
	}

	return options.filter((option) => {
		return [option.label, option.key, option.group, option.description]
			.filter(Boolean)
			.some((value) => value?.toLowerCase().includes(normalizedPattern));
	});
};

export const groupAiVariableOptions = (options: AiVariableOption[]) => {
	return options.reduce<Record<string, AiVariableOption[]>>((accumulator, option) => {
		if (!accumulator[option.group]) {
			accumulator[option.group] = [];
		}

		accumulator[option.group].push(option);

		return accumulator;
	}, {});
};
